import { InputBase, useTheme } from "@mui/material";
import { type KeyboardEvent, useState } from "react";

/** The service rejects anything longer with a 400. */
export const CONVERSATION_TITLE_MAX_LENGTH = 200;

interface ChatRowTitleEditorProps {
  /**
   * The STORED title, not the display label. A thread the service never named
   * opens empty — seeding it with the "Untitled · 4:15 PM" fallback would let
   * the user commit that timestamp as a real name without noticing.
   */
  initialTitle: string | null;
  /** Called only with a title worth sending — trimmed, non-empty, changed. */
  onCommit: (title: string) => void;
  onCancel: () => void;
}

/**
 * Inline rename for a conversation row.
 *
 * Purpose-built rather than reusing `EditableChip`: that component sizes itself
 * `max-content` with a fixed 24px right gutter and hardcodes `subtitle1`, none
 * of which fits a 206px sidebar row, and it keeps its debounce timer in a
 * module-scope variable that two mounted instances would share.
 *
 * Empty, whitespace-only and unchanged values are never sent — the first two
 * are a 400 and the third is a wasted round trip.
 */
export const ChatRowTitleEditor = ({
  initialTitle,
  onCommit,
  onCancel,
}: ChatRowTitleEditorProps) => {
  const theme = useTheme();
  const [value, setValue] = useState(initialTitle ?? "");

  const commit = () => {
    const title = value.trim();
    if (!title || title === (initialTitle ?? "").trim()) {
      onCancel();
      return;
    }
    onCommit(title);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // The row sits inside a scrollable list with its own key handling.
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <InputBase
      autoFocus
      value={value}
      placeholder="Chat name"
      onChange={(event) => setValue(event.target.value)}
      // Clicking away is a commit, matching every other inline rename in the
      // portal. Escape is the way out without saving.
      onBlur={commit}
      onKeyDown={handleKeyDown}
      onFocus={(event) => event.target.select()}
      inputProps={{
        "aria-label": "Chat name",
        maxLength: CONVERSATION_TITLE_MAX_LENGTH,
      }}
      sx={{
        ...theme.typography["body-small"],
        flex: 1,
        minWidth: 0,
        px: "8px",
        py: "2px",
        borderRadius: "4px",
        color: theme.palette.lightBg.high,
        backgroundColor: theme.palette.neutral.main,
        border: `1px solid ${theme.palette.primary.tint}`,
      }}
    />
  );
};
